import { AppUrls } from 'shared/config/model/urls';

import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import useMediaQuery from '@restart/hooks/useMediaQuery';
import { Trans } from '@lingui/react';

import BoxTransparent from 'shared/ui/Box/BoxTransparent/BoxTransparent';
import LoaderCentered from 'shared/ui/LoaderCentered/LoaderCentered';
import Corrected from 'shared/ui/icons/Monitors/Corrected';
import Problem from 'shared/ui/icons/Monitors/Problem';
import Warning from 'shared/ui/icons/Monitors/Warning';
import {HttpMonitorModal, useCanCreateHttpMonitor, ISummaryHttpMonitors} from 'features/http';
import {ExceededModal} from 'entities/user';
import { pluralize } from 'shared/lib/format/pluralize';
import { formatCounter } from 'shared/lib/format/formatCounter';
import { isTranslate} from 'shared/lib/lang';

import BlockInfo from './BlockInfo/BlockInfo';
import MonitorInfo from './BlockInfo/MonitorInfo/MonitorInfo';
import MonitorStatus from './MonitorStatus/MonitorStatus';
import NoIssuesBlock from './NoIssuesBlock/NoIssuesBlock';
import PulseCircles from './PulseCircles/PulseCircles';
import SummaryHeader from '../../../../shared/ui/SummaryHeader/SummaryHeader';
import AddMonitorButton from './AddMonitorButton/AddMonitorButton';
import CheckBlock from './NoIssuesBlock/CheckBlock';

import styles from './SummaryMonitors.module.scss';

interface IMonitorsSummaryProps {
    summary: ISummaryHttpMonitors,
    isFetching: boolean,
}

const SummaryMonitors: React.FC<IMonitorsSummaryProps> = ({summary, isFetching}) => {
    const canCreateMonitor = useCanCreateHttpMonitor();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExceededOpen, setIsExceededOpen] = useState(false);

    const {up, down, warning, paused} = summary;
    const isDesktop = useMediaQuery('(min-width: 621px)');

    const handleClose = () => {
        setIsModalOpen(false);
        setIsExceededOpen(false);
    };

    const handleOpen = () => {
        setIsModalOpen(true);
        setIsExceededOpen(true);
    };

    const shouldShowNoIssues = useMemo(
        () => down === 0 && warning === 0 && up > 0,
        [down, warning, up],
    );

    const upIsZero = Number(up || 0) === 0;
    const downIsZero = Number(down || 0) === 0;
    const warningIsZero = Number(warning || 0) === 0;
    const isNoMonitors = summary.total_active === 0;
    const hasMonitorsWithUnknownStatus = useMemo(
        () => summary.total_active > 0 && up === 0 && warning === 0 && down === 0,
        [summary.total_active, up, warning, down],
    );

    return (
        <BoxTransparent className={cn(
            styles.wrapper,
            {
                [styles.backgroundWarning]: upIsZero && downIsZero && !warningIsZero,
                [styles.backgroundProblem]: upIsZero && warningIsZero && !downIsZero,
            },
            styles[isTranslate() ? 'enBackground' : 'ruBackground'],
        )}>
            <SummaryHeader />
            <LoaderCentered active={isFetching}/>
            <div className={cn(
                styles.title,
                styles[isTranslate() ? 'enColorTitle' : 'ruColorTitle'],
            )}>
                <Trans id="home.title" message="Мониторинг" />
            </div>
            <div className={styles.monitors_wrapper}>
                <div className={cn(
                    styles.monitors_container,
                    isNoMonitors && styles.notSummary,
                )}>
                    {hasMonitorsWithUnknownStatus && <CheckBlock />}
                    {
                        up > 0 && (
                            <MonitorStatus
                                text={pluralize(up, {
                                    one: <Trans id="monitor.one.correct" message="Исправный" />,
                                    few: <Trans id="monitor.few.correct" message="Исправных" />,
                                    many: <Trans id="monitor.many.correct" message="Исправных" />,
                                })}
                                counter={formatCounter(up, isDesktop)}
                                className={cn(
                                    styles.statusMonitor,
                                    styles[isTranslate() ? 'enCorrectedBig' : 'ruCorrectedBig'],
                                )}
                                icon={<Corrected/>}
                                link={AppUrls.HTTP_MONITOR_CORRECT}
                                color={styles.color}
                                textColor={styles.textColor}
                            />
                        )
                    }

                    {
                        warning > 0 && (
                            <div className={styles.monitorItem}>
                                <PulseCircles color={styles.colorWarning} animate={downIsZero}/>
                                <MonitorStatus
                                    text={pluralize(warning, {
                                        one: <Trans id="monitor.one.warning" message="Предупреждение" />,
                                        few: <Trans id="monitor.few.warning" message="Предупреждения" />,
                                        many: <Trans id="monitor.many.warning" message="Предупреждений" />,
                                    })}
                                    counter={formatCounter(warning, isDesktop)}
                                    className={cn(
                                        styles.statusMonitor,
                                        upIsZero
                                            ? styles[isTranslate() ? 'enWarningBig' : 'ruWarningBig']
                                            : styles[isTranslate() ? 'enWarningSmall' : 'ruWarningSmall'],
                                    )}
                                    icon={<Warning />}
                                    link={AppUrls.DOMAIN_INCIDENTS}
                                    color={styles.color}
                                    textColor={styles.textColor}
                                />
                            </div>
                        )
                    }

                    {
                        down > 0 && (
                            <div className={styles.monitorItem}>
                                <PulseCircles color={styles.colorProblem} animate={true}/>
                                <MonitorStatus
                                    text={pluralize(down, {
                                        one: <Trans id="monitor.one.problem" message="Проблема" />,
                                        few: <Trans id="monitor.few.problem" message="Проблемы" />,
                                        many: <Trans id="monitor.many.problem" message="Проблем" />,
                                    })}
                                    counter={formatCounter(down, isDesktop)}
                                    className={cn(
                                        styles.statusMonitor,
                                        !downIsZero && upIsZero && warningIsZero
                                            ? styles[isTranslate() ? 'enProblemBig' : 'ruProblemBig']
                                            : styles[isTranslate() ? 'enProblemSmall' : 'ruProblemSmall'],
                                    )}
                                    icon={<Problem />}
                                    link={AppUrls.HTTP_MONITOR_PROBLEM}
                                    color={styles.color}
                                    textColor={styles.textColor}
                                />
                            </div>
                        )
                    }
                    {shouldShowNoIssues && <NoIssuesBlock />}
                    <MonitorInfo classname={styles.paused_mob} counter={paused} isLink={summary.paused > 0}/>
                </div>
                <AddMonitorButton onClick={handleOpen} />
            </div>
            <BlockInfo summary={summary}/>
            <AddMonitorButton onClick={handleOpen} mobile/>
            {
                canCreateMonitor
                    ? <HttpMonitorModal open={isModalOpen} handleClose={handleClose}/>
                    : <ExceededModal
                        isOpen={isExceededOpen}
                        handleClose={handleClose}
                    />
            }
        </BoxTransparent>
    );
};

export default SummaryMonitors;